import { useEffect } from 'react'

import { cn } from '@/lib/html/class-name'

interface ScrollBarProps {
  children: React.ReactNode
  className?: string
}

export default function ScrollBar({ children, className }: ScrollBarProps) {
  if (!children) {
    return null
  }

  return (
    <div
      className={cn(
        'overflow-y-auto pr-2',
        '[&::-webkit-scrollbar]:w-2',
        '[&::-webkit-scrollbar]:!block',
        '[&::-webkit-scrollbar-track]:bg-primary-100',
        '[&::-webkit-scrollbar-thumb]:bg-primary-400',
        '[&::-webkit-scrollbar-thumb:hover]:bg-primary',
        '[&::-webkit-scrollbar-thumb]:rounded-full',
        '[&::-webkit-scrollbar-thumb]:border-transparent',
        '[&::-webkit-scrollbar-thumb]:bg-clip-padding',
        className
      )}
      style={{
        scrollbarWidth: 'thin',
        scrollbarColor: '#b09891 #ede6e1',
        WebkitOverflowScrolling: 'touch',
        scrollbarGutter: 'stable',
      }}
    >
      {children}
    </div>
  )
}

interface FullScreenProps {
  children: React.ReactNode
  className?: string
  backgroundColor?: string
}

export function FullScreen({
  children,
  className,
  backgroundColor = 'bg-background',
}: FullScreenProps) {
  // Override body padding for PWA standalone mode to prevent scrolling issues
  useEffect(() => {
    const originalBodyStyle = {
      padding: document.body.style.padding,
      overflow: document.body.style.overflow,
    }

    // Remove body padding and prevent scroll on body
    document.body.style.padding = '0'
    document.body.style.overflow = 'hidden'

    return () => {
      // Restore original styles on cleanup
      document.body.style.padding = originalBodyStyle.padding
      document.body.style.overflow = originalBodyStyle.overflow
    }
  }, [])

  if (!children) {
    return null
  }

  return (
    <div
      className={cn('flex flex-col items-center justify-center', backgroundColor, className)}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        paddingTop: 'env(safe-area-inset-top)',
        paddingLeft: 'max(1rem, env(safe-area-inset-left))',
        paddingRight: 'max(1rem, env(safe-area-inset-right))',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {children}
    </div>
  )
}
